/**
 * パウチ色のキーボードショートカット（計算エリア・値編集用）
 * Caps Lock は e.key を toLowerCase して吸収する想定。
 */

/** 単色指定: キー（小文字1文字）→ POUCH_COLORS の id */
export const POUCH_COLOR_HOTKEY_TO_ID: Record<string, string> = {
  w: 'gray',
  o: 'orange',
  y: 'orange',
  g: 'yellow-green',
  b: 'sky-blue',
  p: 'red-purple',
  r: 'pink',
};

/** ツールチップ表示順（表示用は大文字1文字＋色●。オレンジは Y のみ—O はガイドに出さない） */
export const POUCH_COLOR_HOTKEY_DISPLAY_ORDER: { letter: string; colorId: string }[] = [
  { letter: 'W', colorId: 'gray' },
  { letter: 'Y', colorId: 'orange' },
  { letter: 'G', colorId: 'yellow-green' },
  { letter: 'B', colorId: 'sky-blue' },
  { letter: 'P', colorId: 'red-purple' },
  { letter: 'R', colorId: 'pink' },
];

export type PouchColorHotkeyAction =
  | { type: 'direct'; colorId: string }
  | { type: 'rotate'; reverse: boolean };

export function resolvePouchColorHotkey(e: KeyboardEvent): PouchColorHotkeyAction | null {
  if (e.ctrlKey || e.metaKey || e.altKey) {
    return null;
  }
  if (e.key.length !== 1) {
    return null;
  }
  const ch = e.key.toLowerCase();
  if (ch === 'c') {
    return { type: 'rotate', reverse: e.shiftKey };
  }
  const colorId = POUCH_COLOR_HOTKEY_TO_ID[ch];
  if (colorId) {
    return { type: 'direct', colorId };
  }
  return null;
}

/** テーブル操作中は計算エリアの Delete ホバー削除を無効にする */
export function isTableKeyboardContextActive(): boolean {
  const active = document.activeElement;
  if (active instanceof HTMLElement) {
    if (active.closest('.pouch-table')) {
      return true;
    }
    if (active.closest('[data-table-editing="true"]')) {
      return true;
    }
  }
  if (document.querySelector('.pouch-table .cell-selected')) {
    return true;
  }
  return false;
}

/** ホバー用: フォーカスが編集系のときはホットキーを無効にする */
export function isEditableElement(el: Element | null): boolean {
  if (!el || !(el instanceof HTMLElement)) {
    return false;
  }
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') {
    return true;
  }
  return el.isContentEditable;
}

/**
 * パウチ値入力（handleEditValueChange の replace と同じ許可セット）にそのまま入れてよい1文字か。
 * 色ホットキー用の英字（w,o,y,g,b,p,r,c）は含まない — PouchComponent では色処理の後に呼ぶ。
 */
const VALUE_INPUT_ALLOWED_ONE_CHAR = /^[0-9+\-*/^xX×÷%.:~?='()=]$/;

export function isAllowedPouchValueInputChar(key: string): boolean {
  return key.length === 1 && VALUE_INPUT_ALLOWED_ONE_CHAR.test(key);
}

/** keydown 用: このキーで既定の文字入力を抑止してよいか（Ctrl/Meta/Alt+は別途許可） */
export function shouldSuppressValueFieldKeyInput(e: KeyboardEvent): boolean {
  // IME 変換中・Process(229) は抑止しない（記号・英数がブロックされるのを防ぐ）
  if (e.isComposing) {
    return false;
  }
  if ((e as KeyboardEvent & { keyCode?: number }).keyCode === 229) {
    return false;
  }
  if (e.ctrlKey || e.metaKey || e.altKey) {
    return false;
  }
  if (e.key.length !== 1) {
    return false;
  }
  return !isAllowedPouchValueInputChar(e.key);
}
